import PropTypes from "prop-types";
import { IconButton, Box, MenuItem, Popover, useTheme } from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { useState, useMemo, useEffect } from "react";
import Iconify from "../../../../reusable-components/iconify";
import DebounceTextField from "../../../../reusable-components/debounce-text-field/DebounceTextField";
import { isValidDate } from "../../../../utils/validate";

TicketFilters.propTypes = {
  filters: PropTypes.array,
  setFilters: PropTypes.func,
  tickets: PropTypes.array,
  setIsFilterActive: PropTypes.func,
}

const measureContext = document.createElement('canvas').getContext('2d');

// "start - end", blanking a missing or invalid end; "" when neither end is set
const formatDateRange = (range) => range.some(isValidDate)
  ? range.map(d => isValidDate(d) ? new Date(d).toLocaleDateString() : "").join(" - ")
  : "";

export default function TicketFilters({ filters, setFilters, tickets, setIsFilterActive }) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValues, setInputValues] = useState(() =>
    Object.fromEntries(filters.map(f => {
      if(f.name === 'createdOn' && Array.isArray(f.value)) return [f.name, formatDateRange(f.value)];
      return [f.name, f.value || ""];
    }))
  );
  const facilities = useMemo(() => tickets.map(t => t.facility).filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a.localeCompare(b)), [tickets]);
  const managers = useMemo(() => tickets.flatMap(t => t.facilityManagerAssignments?.map(m => m.manager) ?? []).filter((v, i, a) => v && a.indexOf(v) === i).sort((a, b) => a.localeCompare(b)), [tickets]);

  const theme = useTheme();

  // fields size to the longer of their label and value, measured in the 11px input font
  const getTextWidth = (text) => {
    measureContext.font = `11px ${theme.typography.fontFamily}`;
    return Math.ceil(measureContext.measureText(text).width);
  }

  // the theme font loads with display=swap, so re-render (and re-measure) once it has arrived
  const [, setFontsLoaded] = useState(false);
  useEffect(() => {
    let active = true;
    document.fonts?.ready.then(() => { if (active) setFontsLoaded(true); });
    return () => { active = false; };
  }, []);

  const handlePopoverClose = () => {
    setActiveFilter(null);
    setAnchorEl(null);
  }

  const handleOptionClick = (filterName, value) => {
    setFilters((prevVal) => prevVal.map(f => f.name === filterName ? {...f, value} : f));
    setInputValues((prev) => ({ ...prev, [filterName]: value }));
    handlePopoverClose();
  }

  return <>
    {/* scrolling row (scrollbar hidden); the clear button stays pinned outside it */}
    <Box sx={{
      display: 'flex',
      gap: 1,
      overflowX: 'auto',
      flex: 1,
      minWidth: 0,
      pt: 1,
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }}>
      {filters.map((filter) => (
        <DebounceTextField
          key={filter.name}
          value={inputValues[filter.name] || ""}
          debounceFunc={() => setFilters((prevVal) => prevVal.map(f => f.name === filter.name ? {...f, value: inputValues[filter.name]} : f))}
          size="small"
          onChange={(e) => {
            setInputValues((prevValues) => ({
              ...prevValues,
              [filter.name]: e.target.value
            }));
          }}
          onClick={(e) => {
            setActiveFilter(filter.name);
            setAnchorEl(e.currentTarget);
          }}
          // the date range is only set through the picker; typed text would replace the [start, end] value
          InputProps={{ readOnly: filter.name === 'createdOn' }}
          variant="outlined"
          label={filter.label}
          sx={{
            flexShrink: 0,
            maxWidth: 200,
            '& .MuiInputLabel-root': {
              fontSize: '11px',
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              transform: 'translate(17px, -6px) scale(0.85)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiInputBase-input': {
              fontSize: '11px',
              // the resting label is capped at 100% - 24px (vs 16px input padding), so it needs 8px more;
              // +2px so the measured text never rounds into the ellipsis
              width: `${Math.max(getTextWidth(filter.label) + 8, getTextWidth(inputValues[filter.name] || "")) + 2}px`,
              padding: '8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      ))}
    </Box>
    <IconButton
      sx={{padding: 0}}
      onClick={() => {
        setFilters((prevFilters) => prevFilters.map(f => ({ ...f, value: f.name === 'createdOn' ? [null, null] : '' })));
        setIsFilterActive(false);
      }}
    >
      <Iconify icon="mdi:filter-variant-remove" sx={{ ml: 'auto', mr: 1 }}/>
    </IconButton>
    <Popover
      open={activeFilter === 'createdOn' || activeFilter === 'facility' || activeFilter === 'manager'}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, minWidth: 200 }}>
      {activeFilter === 'createdOn' && (
        <DateRangePicker
          value={filters.find(f => f.name === 'createdOn')?.value}
          onChange={(newValue) => {
            setFilters((prevVal) => prevVal.map(f => f.name === 'createdOn' ? { ...f, value: newValue } : f));
            setInputValues((prev) => ({ ...prev, createdOn: formatDateRange(newValue) }));
          }}
          onClose={handlePopoverClose}
          calendars={1}
          slotProps={{
            // the Date Added field is read-only, so clearing just the range happens here
            actionBar: { actions: ['clear', 'cancel', 'accept'] },
            textField: {
              size: 'small',
              sx: {
                '& .MuiInputBase-root': {
                  fontSize: '0.75rem',
                  height: '30px',
                  minHeight: '30px',
                },
                '& input': {
                  padding: '3px 6px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.7rem',
                },
              },
            },
          }}
        />
      )}
      {(activeFilter === 'facility' || activeFilter === 'manager') && (
        (activeFilter === 'facility' ? facilities : managers)?.map(option => (
          <MenuItem key={option} onClick={() => handleOptionClick(activeFilter, option)}>
            <Box sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}>
              {option}
            </Box>
          </MenuItem>
        ))
      )}
      </Box>
    </Popover>
  </>
}